import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { approvalsApi } from '../lib/api';
import { ROLE_LABELS } from '../types';
import type { CurrentUser } from '../types';
import { useTenantContext } from '../context/TenantContext';

// ── Design tokens ──────────────────────────────────────────────────────────────
// Sidebar stays dark. Everything else is light.
const C = {
  // Page / surface
  canvas:   '#f8fafc',
  surface:  '#ffffff',
  surface2: '#f1f5f9',
  border:   '#e2e8f0',
  // Text
  textPrimary:   '#0f172a',
  textSecondary: '#475569',
  textMuted:     '#94a3b8',
  // Primary action
  primary:     '#0ea5e9',
  primaryDark: '#0284c7',
  // Semantic
  red: '#ef4444',
  // Sidebar (dark)
  sidebar:    '#0f172a',
  sideBorder: '#1e293b',
  sideText:   '#94a3b8',
  sideMuted:  '#475569',
} as const;

const SANS = "'Roboto', system-ui, sans-serif";
const MONO = "'Courier New', monospace";

// ── Shared CSS injected once ───────────────────────────────────────────────────
const SHELL_CSS = `
  /* Sidebar nav item hover — dark surface */
  .sl-nav-item:hover     { background: rgba(255,255,255,0.06) !important; color: #e2e8f0 !important; }
  .sl-user-row:hover     { background: rgba(255,255,255,0.06) !important; cursor: pointer; }
  .sl-client-chip:hover  { border-color: rgba(14,165,233,0.3) !important; }

  /* Light surface hovers */
  .sl-stat-card:hover    { border-color: #cbd5e1 !important; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04) !important; }
  .sl-quick-card:hover   { border-color: #0ea5e9 !important; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.10), 0 4px 8px rgba(0,0,0,0.04) !important; }
  .sl-tr:hover td        { background: #f8fafc; }
  .sl-tr:last-child td   { border-bottom: none !important; }
  .sl-btn-ghost:hover    { background: #f8fafc !important; color: #0f172a !important; }
  .sl-btn-primary:hover  { background: #0284c7 !important; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(14,165,233,0.3) !important; }

  /* SuperAdmin table hovers */
  .sa-tr:hover td        { background: #f8fafc; }
  .sa-tr.offboarded      { opacity: 0.55; }
  .sa-btn:hover:not(:disabled)       { filter: brightness(1.08); transform: translateY(-1px); }
  .sa-btn:disabled                   { opacity: 0.4; cursor: not-allowed; }
  .sa-btn-ghost:hover:not(:disabled) { background: #f1f5f9 !important; color: #0f172a !important; }
  .sa-input:focus { border-color: #0ea5e9 !important; box-shadow: 0 0 0 3px rgba(14,165,233,0.15); outline: none; }

  @keyframes sa-spin { to { transform: rotate(360deg); } }

  ::-webkit-scrollbar       { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

// ── Types ──────────────────────────────────────────────────────────────────────
interface AppShellProps {
  user: CurrentUser;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

// ── AppShell ───────────────────────────────────────────────────────────────────
export default function AppShell({ user, title, subtitle, actions, children }: AppShellProps) {
  const { pathname } = useLocation();
  const { tenants, selectedTenantId, selectedTenant, setSelectedTenantId } = useTenantContext();
  const [pendingCount, setPendingCount] = useState(0);

  const [menuOpen, setMenuOpen]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const canApprove   = ['cfo', 'procurement_head', 'procurement_manager', 'procurement_executive'].includes(user.role);
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    if (canApprove) {
      approvalsApi.inbox().then((s) => setPendingCount(s.length)).catch(() => {});
    }
  }, [canApprove]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const safeEmail   = user.email ?? '';
  const initials    = user.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : safeEmail.slice(0, 2).toUpperCase() || '?';
  const emailDomain = safeEmail.split('@')[1] ?? safeEmail;

  const workspaceName = (isSuperAdmin && selectedTenant) ? selectedTenant.name : 'SpendLens';
  const workspaceMeta = (isSuperAdmin && selectedTenant) ? selectedTenant.slug : emailDomain;

  const activePage =
    pathname.startsWith('/superadmin')         ? 'superadmin' :
    pathname.startsWith('/savings')            ? 'savings'    :
    pathname.startsWith('/vendors')            ? 'vendors'    :
    pathname.startsWith('/approvals')          ? 'approvals'  :
    pathname.startsWith('/purchase-requests')  ? 'requests'   :
    'dashboard';

  const showTopbarRight = actions != null || (isSuperAdmin && tenants.length > 0);

  return (
    <>
      <style>{SHELL_CSS}</style>

      <div style={{
        display: 'flex', minHeight: '100vh',
        background: C.canvas, color: C.textPrimary,
        fontFamily: SANS, fontWeight: 300, fontSize: 14, overflowX: 'hidden',
      }}>

        {/* ── Sidebar (dark) ─────────────────────────── */}
        <aside style={{
          width: 220, background: C.sidebar, borderRight: `1px solid ${C.sideBorder}`,
          display: 'flex', flexDirection: 'column', flexShrink: 0,
          position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', zIndex: 2,
        }}>

          {/* Logo + workspace chip */}
          <div style={{ padding: '24px 20px 20px', borderBottom: `1px solid ${C.sideBorder}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, boxShadow: '0 0 16px rgba(14,165,233,0.3)',
              }}>◈</div>
              <div style={{ fontSize: 16, fontWeight: 300, letterSpacing: '-0.3px', color: '#ffffff' }}>
                Spend<span style={{ color: C.primary }}>Lens</span>
              </div>
            </div>

            <div className="sl-client-chip" style={{
              background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.1)`,
              borderRadius: 8, padding: '10px 12px', transition: 'border-color 0.2s',
            }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.sideMuted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 3 }}>
                Workspace
              </div>
              <div style={{ fontSize: 13, fontWeight: 400, color: '#ffffff' }}>{workspaceName}</div>
              <div style={{ fontSize: 11, color: C.sideText, fontFamily: MONO }}>{workspaceMeta}</div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
            <NavSection label="Workspace">
              <NavItem icon="⬡" label="Dashboard"  to="/dashboard"         active={activePage === 'dashboard'} />
              <NavItem icon="◫" label="Requests"   to="/purchase-requests" active={activePage === 'requests'} />
              {canApprove && (
                <NavItem icon="◎" label="Approvals" to="/approvals/inbox"
                  active={activePage === 'approvals'}
                  badge={pendingCount > 0 ? pendingCount : undefined}
                  badgeRed
                />
              )}
            </NavSection>
            <NavSection label="Intelligence">
              <NavItem icon="◈" label="Vendors" to="/vendors" active={activePage === 'vendors'} />
              {(canApprove || isSuperAdmin) && (
                <NavItem icon="◉" label="Savings" to="/savings" active={activePage === 'savings'} />
              )}
            </NavSection>
            {isSuperAdmin && (
              <NavSection label="Admin">
                <NavItem icon="⊞" label="SuperAdmin" to="/superadmin" active={activePage === 'superadmin'} />
              </NavSection>
            )}
          </nav>

          {/* User row */}
          <div style={{ padding: '14px 12px', borderTop: `1px solid ${C.sideBorder}`, position: 'relative' }}>
            <div
              className="sl-user-row"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8 }}
              onClick={() => setMenuOpen((o) => !o)}
              title="Account menu"
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #0ea5e9, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 600, color: 'white',
              }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 400, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name || user.email}
                </div>
                <div style={{ fontSize: 11, color: C.sideText }}>{ROLE_LABELS[user.role]}</div>
              </div>
              <span style={{ fontSize: 9, color: C.sideMuted, marginLeft: 'auto' }}>▲</span>
            </div>
          </div>
        </aside>

        {/* ── Main content ──────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>

          {/* Topbar */}
          <div style={{
            height: 56, borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', padding: '0 28px', gap: 16,
            background: C.surface,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            position: 'sticky', top: 0, zIndex: 10, flexShrink: 0,
          }}>
            <div>
              <span style={{ fontSize: 18, fontWeight: 400, color: C.textPrimary, letterSpacing: '-0.3px' }}>{title}</span>
              {subtitle && (
                <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 6 }}>· {subtitle}</span>
              )}
            </div>
            {showTopbarRight && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                {isSuperAdmin && tenants.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>Client</span>
                    <select
                      value={selectedTenantId ?? ''}
                      onChange={(e) => setSelectedTenantId(e.target.value)}
                      style={{
                        backgroundColor: C.surface, border: `1px solid ${C.border}`,
                        borderRadius: 8, padding: '4px 10px',
                        color: C.textPrimary, fontSize: 12.5, cursor: 'pointer', outline: 'none',
                        fontFamily: SANS, fontWeight: 300,
                      }}
                    >
                      {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                {actions && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {actions}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Page content */}
          <div style={{ flex: 1, padding: 28 }}>
            {children}
          </div>
        </div>
      </div>

      {/* ── User menu popover ──────────────────────────── */}
      {menuOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed', bottom: 80, left: 12,
            width: 196, zIndex: 50,
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 10, overflow: 'hidden',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
              {user.name || user.email}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 7, fontFamily: MONO }}>
              {user.email}
            </div>
            <span style={{
              display: 'inline-block', fontSize: 9.5, fontWeight: 500,
              letterSpacing: '0.5px', textTransform: 'uppercase',
              padding: '2px 7px', borderRadius: 4,
              background: '#e0f2fe', color: '#0369a1',
            }}>
              {ROLE_LABELS[user.role]}
            </span>
          </div>

          <button
            onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}
            style={{
              width: '100%', textAlign: 'left', padding: '9px 14px',
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12.5, color: C.red, transition: 'background 0.1s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <span style={{ fontSize: 12 }}>⏻</span>
            Sign out
          </button>
        </div>
      )}

      {/* ── Logout confirmation ────────────────────────── */}
      {confirmOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: '1.75rem 2rem', width: 340,
            boxShadow: '0 24px 60px rgba(0,0,0,0.12)',
          }}>
            <p style={{ fontSize: '1rem', fontWeight: 400, color: C.textPrimary, margin: '0 0 0.4rem', letterSpacing: '-0.2px' }}>
              Sign out of SpendLens?
            </p>
            <p style={{ fontSize: '0.82rem', color: C.textSecondary, margin: '0 0 1.5rem', lineHeight: 1.55 }}>
              You will need to verify your email again to sign back in.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  padding: '0.45rem 1rem', borderRadius: 7, background: 'transparent',
                  border: `1px solid ${C.border}`, color: C.textSecondary,
                  fontSize: 12.5, cursor: 'pointer', fontFamily: SANS,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { window.location.href = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/auth/logout`; }}
                style={{
                  padding: '0.45rem 1.1rem', borderRadius: 7,
                  background: C.red, border: 'none',
                  color: '#fff', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                  fontFamily: SANS,
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── NavSection ─────────────────────────────────────────────────────────────────
function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '0 12px', marginBottom: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: '#475569', letterSpacing: '0.8px', textTransform: 'uppercase', padding: '12px 8px 6px' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ── NavItem ────────────────────────────────────────────────────────────────────
function NavItem({ icon, label, to, active = false, badge, badgeRed = false }: {
  icon: string; label: string; to: string; active?: boolean; badge?: number; badgeRed?: boolean;
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div className={active ? undefined : 'sl-nav-item'} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 10px 9px 8px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
        fontSize: 13.5, fontWeight: 300, transition: 'all 0.15s',
        color: active ? '#0ea5e9' : '#94a3b8',
        background: active ? 'rgba(14,165,233,0.12)' : 'transparent',
        borderLeft: active ? '2px solid #0ea5e9' : '2px solid transparent',
      }}>
        <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{icon}</span>
        {label}
        {badge !== undefined && (
          <span style={{
            marginLeft: 'auto', fontSize: 10, fontWeight: 700,
            padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center',
            background: badgeRed ? '#ef4444' : '#0ea5e9',
            color: '#ffffff',
          }}>{badge}</span>
        )}
      </div>
    </Link>
  );
}
